import React, { useState, useRef, useEffect } from 'react';
import '../styles/chat-widget.css';
import { FaStar, FaCommentDots, FaPaperPlane } from 'react-icons/fa';
import { FaMoneyBillWave } from 'react-icons/fa';
import LoginModal from './LoginModal';
import { useChatWidget } from '../context/ChatWidgetContext';

function ChatWidget() {
    const { isLarge, isOpen, closeChat } = useChatWidget();
    // Random assistant names
    const assistantNames = ['Anna', 'Sarah', 'Emma', 'Sophie', 'Mia', 'Lily', 'Grace', 'Chloe', 'Zoe', 'Ava'];
    
    // Get random assistant name
    const getRandomAssistantName = () => {
        return assistantNames[Math.floor(Math.random() * assistantNames.length)];
    };

    const [assistantName] = useState(getRandomAssistantName());
    const [messages, setMessages] = useState([
        { text: `Hi, I'm ${assistantName}, your virtual assistant. Tell me your symptoms, and I'll connect you with the right practitioner.`, sender: 'bot' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const [open, setOpen] = useState(false); // Local state for normal chat widget
    const [bookingLoading, setBookingLoading] = useState(null); // doctor email for which booking is loading
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [pendingDoctorEmail, setPendingDoctorEmail] = useState(null);
    const [cookiesHandled, setCookiesHandled] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const checkCookiesAndShowChat = () => {
        const cookieConsent = localStorage.getItem('cookieConsent');
        if (cookieConsent) {
            setCookiesHandled(true);
            // Small delay to ensure smooth transition
            setTimeout(() => {
                setOpen(true);
            }, 500);
        }
        setInitialized(true);
    };

    // Check if cookies have been handled with a small delay to prevent glitch
    useEffect(() => {
        // Add a small delay to prevent the glitch on page reload
        const timer = setTimeout(checkCookiesAndShowChat, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // Listen for localStorage changes (when cookies are accepted)
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'cookieConsent' && e.newValue) {
                // Cookies were just set, show the chat widget
                setTimeout(() => {
                    setCookiesHandled(true);
                    setOpen(true);
                }, 300);
            }
        };

        // Listen for storage events (works across tabs)
        window.addEventListener('storage', handleStorageChange);

        // Also listen for custom events (for same-tab updates)
        const handleCustomEvent = () => {
            setTimeout(() => {
                setCookiesHandled(true);
        setOpen(true);
            }, 300);
        };
        
        window.addEventListener('cookiesAccepted', handleCustomEvent);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('cookiesAccepted', handleCustomEvent);
        };
    }, []);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const sendMessage = async () => {
        if (!input.trim()) return;
        const userMessage = { text: input, sender: 'user' };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);
        
        // Keep focus on input field
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
        
        try {
            const response = await fetch('https://chat.virtualtriage.ca/chat/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    query: input,
                    session_id: sessionStorage.getItem('session_id') || (() => {
                        const newSessionId = Date.now().toString();
                        sessionStorage.setItem('session_id', newSessionId);
                        return newSessionId;
                    })()
                })
            });
            const data = await response.json();
            const newMessages = [{ text: data.message || 'No response from server.', sender: 'bot' }];
            
            // Add suggested responses if available
            if (data.suggested_responses && data.suggested_responses.length > 0) {
                newMessages.push({
                    type: 'suggested_responses',
                    responses: data.suggested_responses
                });
            }
            
            if (Array.isArray(data.doctors) && data.doctors.length > 0) {
                    newMessages.push({
                    type: 'doctors',
                    doctors: data.doctors.map(doctor => ({
                        Full_Name: doctor.Full_Name,
                        Specialization: doctor.doctor_speciality,
                        Rating: doctor.doctor_rating,
                        Image: doctor.doctor_image_url,
                        Fee: doctor.doctor_fees,
                        Email: doctor.doctor_email
                    }))
                });
            }
            setMessages(prev => [...prev, ...newMessages]);
        } catch (error) {
            setMessages(prev => [...prev, { text: 'Sorry, there was an error connecting to the chat server.', sender: 'bot' }]);
        } finally {
            setLoading(false);
            // Keep focus on input field after response
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 0);
        }
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent default to avoid form submission
            sendMessage();
        }
    };

    // Handle suggested response click
    const handleSuggestedResponse = (response) => {
        // Add the suggested response as a user message immediately
        const userMessage = { text: response, sender: 'user' };
        setMessages(prev => [...prev, userMessage]);
        
        // Send the message to the API
        sendSuggestedMessage(response);
    };

    // Send suggested message to API
    const sendSuggestedMessage = async (messageText) => {
        setLoading(true);
        
        try {
            const response = await fetch('https://chat.virtualtriage.ca/chat/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    query: messageText,
                    session_id: sessionStorage.getItem('session_id') || (() => {
                        const newSessionId = Date.now().toString();
                        sessionStorage.setItem('session_id', newSessionId);
                        return newSessionId;
                    })()
                })
            });
            const data = await response.json();
            const newMessages = [{ text: data.message || 'No response from server.', sender: 'bot' }];
            
            // Add suggested responses if available
            if (data.suggested_responses && data.suggested_responses.length > 0) {
                newMessages.push({
                    type: 'suggested_responses',
                    responses: data.suggested_responses
                });
            }
            
            if (Array.isArray(data.doctors) && data.doctors.length > 0) {
                newMessages.push({
                    type: 'doctors',
                    doctors: data.doctors.map(doctor => ({
                        Full_Name: doctor.Full_Name,
                        Specialization: doctor.doctor_speciality,
                        Rating: doctor.doctor_rating,
                        Image: doctor.doctor_image_url,
                        Fee: doctor.doctor_fees,
                        Email: doctor.doctor_email
                    }))
                });
            }
            setMessages(prev => [...prev, ...newMessages]);
        } catch (error) {
            setMessages(prev => [...prev, { text: 'Sorry, there was an error connecting to the chat server.', sender: 'bot' }]);
        } finally {
            setLoading(false);
            // Keep focus on input field after response
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 0);
        }
    };

    const handleBookAppointment = async (doctorEmail) => {
        setBookingLoading(doctorEmail);
        try {
            const response = await fetch("https://staging.virtualtriage.ai/api/v1/patient/appointment/book-appointment/directory/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
                body: JSON.stringify({
                    doctor_email: doctorEmail
                }),
            });
            const data = await response.json();
            if (data.status) {
                alert("Appointment booked successfully!");
            } else if (data.clinic_id) {
                setTimeout(() => {
                    window.location.href = `https://patient.virtualtriage.ai/clinic-details/${data.clinic_id}`;
                }, 200);
            } else {
                alert(data.message || "Failed to book appointment.");
            }
        } catch (error) {
            alert("Error booking appointment.");
        } finally {
            setBookingLoading(null);
        }
    };

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
        if (pendingDoctorEmail) {
            handleBookAppointment(pendingDoctorEmail);
            setPendingDoctorEmail(null);
        }
    };

    // Handle chat close with API call
    const handleCloseChatLocal = async () => {
        try {
            const sessionId = sessionStorage.getItem('session_id');
            if (sessionId) {
                await fetch('https://chat.virtualtriage.ca/chat/close', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: sessionId })
                });
            }
        } catch (error) {
            console.error('Error closing chat session:', error);
        } finally {
            // Clear the chat messages and close the widget
            setMessages([
                { text: `Hi, I'm ${assistantName}, your virtual assistant. If you have any symptoms, let me know so I can connect you with the right practitioner.`, sender: 'bot' }
            ]);
            // Use context closeChat if in large mode, otherwise use local state
            if (isLarge) {
                closeChat();
            } else {
                setOpen(false);
            }
        }
    };

    // Don't render if cookies haven't been handled yet or component hasn't initialized
    if (!cookiesHandled || !initialized) {
        return null;
    }

    // Determine if chat should be open (either from context or local state)
    const shouldShowChat = isLarge ? isOpen : open;
    
    // Handle mini widget click
    const handleMiniWidgetClick = () => {
        if (isLarge) {
            // If we're in large mode, close it and open small
            closeChat();
            setOpen(true);
        } else {
            // Normal small widget behavior
            setOpen(true);
        }
    };

    // Show mini widget when closed
    if (!shouldShowChat) {
        console.log('Rendering mini widget, open state:', shouldShowChat);
        return (
            <div 
                className="chat-widget-mini" 
                onClick={handleMiniWidgetClick}
                style={{
                    position: 'fixed',
                    bottom: '32px',
                    right: '32px',
                    width: '120px',
                    height: '50px',
                    borderRadius: '25px',
                    background: 'linear-gradient(135deg, #50cada 0%, #3bb8c8 100%)',
                    boxShadow: '0 12px 40px rgba(80, 202, 218, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    zIndex: 3000,
                    color: 'white',
                    fontSize: '20px',
                    gap: '8px',
                    padding: '0 16px',
                    transition: 'all 0.3s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontWeight: '600'
                }}
            >
                <FaCommentDots style={{ fontSize: '24px' }} />
                <span>Chat</span>
            </div>
        );
    }

    return (
        <>
            {isLarge && (
                <div 
                    className="chat-widget-backdrop"
                    onClick={handleCloseChatLocal}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 3999,
                        backdropFilter: 'blur(4px)'
                    }}
                />
            )}
            <div className={`chat-widget ${isLarge ? 'chat-widget-large' : ''}`} style={isLarge ? {
                width: '80vw',
                height: '80vh',
                maxWidth: '1200px',
                maxHeight: '800px',
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 4000,
                borderRadius: '20px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            } : {}}>
                {/* Header */}
                <div className="chat-widget-header">
                    <div className="chat-widget-header-logo">
                        <img 
                            src="/assets/Virtual Triage.png" 
                            alt="Virtual Triage Logo" 
                        />
                    </div>
                    <div className="chat-widget-header-text">
                        <h3>Virtual Assistant</h3>
                        <div className="chat-widget-online-status">
                            <div className="online-dot"></div>
                            <span>24/7 Online</span>
                        </div>
                    </div>
                    <div className="chat-widget-header-actions">
                        {!isLarge && (
                            <button 
                                className="header-minimize-btn"
                                onClick={() => {
                                    console.log('Minimize button clicked, setting open to false');
                                    setOpen(false);
                                }} 
                                title="Minimize"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </button>
                        )}
                        <button 
                            className="header-close-btn"
                            onClick={handleCloseChatLocal} 
                            title="Close"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Messages */}
                <div className="chat-widget-messages">
                    {messages.map((msg, idx) => (
                        <div key={`msg-${idx}`} className="message-container">
                            {msg.type === 'doctors' ? (
                                <div className="chat-widget-doctor-profile">
                                    {msg.doctors.map((doctor, doctorIdx) => (
                                        <div key={`doctor-${doctorIdx}`} className="doctor-card">
                                            <div className="doctor-avatar">
                                    <img 
                                                    src={doctor.Image} 
                                                    alt={doctor.Full_Name}
                                        className="chat-widget-doctor-img"
                                    />
                                            </div>
                                    <div className="doctor-info">
                                                <h3>{doctor.Full_Name}</h3>
                                                <p className="specialization">{doctor.Specialization}</p>
                                        <div className="doctor-details">
                                                    <span className="rating">
                                                        <FaStar style={{ color: '#f7b500', marginRight: 4 }} />
                                                        {doctor.Rating}
                                                    </span>
                                                    <span className="fee">
                                                        <FaMoneyBillWave style={{ color: '#22c1d6', marginRight: 4 }} />
                                                        ${doctor.Fee}
                                                    </span>
                                                </div>
                                                <button
                                                    className="chat-widget-book-btn"
                                                    onClick={() => handleBookAppointment(doctor.Email)}
                                                    disabled={bookingLoading === doctor.Email}
                                                >
                                                    {bookingLoading === doctor.Email ? (
                                                        <span className="loading-spinner"></span>
                                                    ) : (
                                                        'Book Appointment'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : msg.type === 'suggested_responses' ? (
                                <div className="suggested-responses">
                                    {msg.responses.map((response, idx) => (
                                        <button
                                            key={`response-${idx}`}
                                            className="suggested-response-btn"
                                            onClick={() => handleSuggestedResponse(response)}
                                        >
                                            {response}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className={`chat-widget-message${msg.sender === 'user' ? ' user' : ' bot'}`}>
                                    {msg.sender === 'bot' && (
                                        <div className="bot-avatar">
                                            <img 
                                                src="/assets/Virtual Triage.png" 
                                                alt="Virtual Triage" 
                                                style={{
                                                    width: '24px',
                                                    height: '24px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="message-content">
                                    <p>{msg.text}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="chat-widget-input">
                    <div className="input-container">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Type your message here..."
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        disabled={loading}
                    />
                    <button 
                        onClick={sendMessage} 
                        disabled={loading || !input.trim()}
                        className="send-button"
                        title="Send message"
                    >
                            {loading ? (
                                <span className="loading-spinner"></span>
                            ) : (
                                <FaPaperPlane />
                            )}
                    </button>
                    </div>
                </div>
            </div>
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                onLoginSuccess={handleLoginSuccess}
            />
        </>
    );
}

export default ChatWidget;